/**
 * 展示层格式化 — 时间 / 数值 / 标识符。
 *
 * 契约：openapi.yaml 的所有时间戳是 **epoch 秒（int64, UTC）**，不是毫秒。
 * 这是最容易出现「沉默逻辑错误」的地方（失效模式 #2）：把秒当毫秒会把 2026 年
 * 渲染成 1970 年，页面照样不报错。因此转换只允许经过本文件，且带范围断言。
 */

/** 合理的 epoch 秒区间：2001-09-09 ~ 2286-11-20。落在毫秒量级会被这道闸门拦下 */
const MIN_EPOCH_S = 1_000_000_000;
const MAX_EPOCH_S = 9_999_999_999;

export function epochSecondsToDate(ts: number): Date {
  if (!Number.isFinite(ts)) {
    throw new TypeError(`时间戳不是有限数值：${String(ts)}`);
  }
  if (ts > MAX_EPOCH_S) {
    throw new RangeError(
      `时间戳 ${ts} 超出 epoch 秒范围，疑似传入了毫秒。接口契约为 epoch 秒（openapi.yaml）。`,
    );
  }
  if (ts < MIN_EPOCH_S && ts !== 0) {
    throw new RangeError(`时间戳 ${ts} 小于 2001-09-09，疑似单位错误或脏数据。`);
  }
  return new Date(ts * 1000);
}

const dateTimeFmt = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dateFmt = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** 绝对时间，用于 title 属性与详情页（永远给得出完整值） */
export function formatDateTime(ts: number | null): string {
  if (ts === null) return '—';
  return dateTimeFmt.format(epochSecondsToDate(ts));
}

export function formatDate(ts: number | null): string {
  if (ts === null) return '—';
  return dateFmt.format(epochSecondsToDate(ts));
}

const SECOND = 1;
const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

export interface RelativeTime {
  /** 「6 天后到期」「3 分钟前」这类可直接渲染的文本 */
  readonly text: string;
  /** 距现在的秒数；正 = 未来，负 = 过去 */
  readonly deltaSeconds: number;
}

/**
 * 相对时间。UI 规范只在两处使用：到期 < 7 天（P-10）、最近心跳（P-13）。
 * 其余场景一律绝对时间——相对时间会让运营者失去可核对的锚点。
 */
export function relativeTime(ts: number, now: number = Math.floor(Date.now() / 1000)): RelativeTime {
  const delta = ts - now;
  const abs = Math.abs(delta);
  const suffix = delta >= 0 ? '后' : '前';

  let magnitude: string;
  if (abs < MINUTE * SECOND) {
    magnitude = `${Math.max(abs, 1)} 秒`;
  } else if (abs < HOUR) {
    magnitude = `${Math.floor(abs / MINUTE)} 分钟`;
  } else if (abs < DAY) {
    magnitude = `${Math.floor(abs / HOUR)} 小时`;
  } else if (abs < DAY * 30) {
    magnitude = `${Math.floor(abs / DAY)} 天`;
  } else {
    magnitude = `${Math.floor(abs / (DAY * 30))} 个月`;
  }

  return { text: `${magnitude}${suffix}`, deltaSeconds: delta };
}

/** 到期列：< 7 天走相对时间并标记紧迫，否则绝对日期（03-UIUX.md §5 P-10） */
export const EXPIRY_SOON_SECONDS = 7 * DAY;

export interface ExpiryDisplay {
  readonly text: string;
  readonly title: string;
  /** 7 天内到期或已过期 */
  readonly urgent: boolean;
}

export function formatExpiry(
  expiresAt: number | null,
  now: number = Math.floor(Date.now() / 1000),
): ExpiryDisplay {
  if (expiresAt === null) {
    return { text: '永久', title: '该授权无到期时间（套餐 duration_days = null）', urgent: false };
  }
  const absolute = formatDateTime(expiresAt);
  const delta = expiresAt - now;
  if (delta <= 0) {
    return { text: `${relativeTime(expiresAt, now).text}过期`, title: absolute, urgent: true };
  }
  if (delta <= EXPIRY_SOON_SECONDS) {
    return { text: `${relativeTime(expiresAt, now).text}到期`, title: absolute, urgent: true };
  }
  return { text: formatDate(expiresAt), title: absolute, urgent: false };
}

/**
 * 标识符中间省略：`LIC-A3F2…9C1D`（03-UIUX.md §5.1 Edge 态）。
 * 用 U+2026 单字符省略号而非三个点，避免与真实数据里的点混淆。
 */
export function middleTruncate(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

const numberFmt = new Intl.NumberFormat('zh-CN');

export function formatCount(n: number): string {
  return numberFmt.format(n);
}

/** 大表不给精确总数（§6.4 分页）。超过阈值时诚实地说「> N」 */
export function formatTotal(total: number, threshold = 10_000): string {
  return total > threshold ? `> ${formatCount(threshold)} 条，请缩小筛选范围` : `${formatCount(total)} 条`;
}
